import { rem } from 'polished'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import FilterChip from 'LuxKit/Chips/FilterChip'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import Pane from 'components/Common/Pane'
import WalledContent from 'components/Common/WalledContent'
import OfferBookButton from 'components/OfferPage/OfferBookButtons/OfferBookButton'
import useIsAvailableForPurchase from 'components/OfferPage/hooks/useIsAvailableForPurchase'
import Group from 'components/utils/Group'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { formatOccupantsAsGuests } from 'lib/offer/occupancyUtils'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { generateLoyaltyPointsCalculatorVillasOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import {
  getCheapestSelectedPackageOptionWithDefault,
  getCurrentDateLabel,
  getFromDurationLabel,
} from 'selectors/offerPage/offerPageSelectors'
import zIndex from 'styles/tools/z-index'

import useVillasPrice from './useVillasPrice'

const Footer = styled(Pane)`
  padding: ${rem(20)};
  position: fixed;
  bottom: 0;
  inset-inline: 0;
  width: 100%;
  z-index: ${zIndex.stickyFooter};
  box-shadow: ${props => props.theme.shadow.top.small};
`

interface Props {
  packageView: App.OfferPackageOptionView
  dateLabel: string
  durationLabel: string
  rooms?: Array<App.Occupants>
  checkInDate?: string
  offer: App.VillaOffer
}

function VillasPageStickyFooter(props: Props) {
  const {
    packageView,
    dateLabel,
    durationLabel,
    checkInDate,
    rooms,
    offer,
  } = props

  const intl = useIntl()
  const [price, value, discount] = useVillasPrice(packageView)
  const availableForPurchase = useIsAvailableForPurchase()
  const dispatch = useOfferPageDispatch()
  const showPrices = packageView.capacityValid && packageView.hotelAvailable && availableForPurchase
  const canBook = !!(checkInDate && rooms && availableForPurchase)
  const onGuestsClick = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [dispatch])

  const onDatesClick = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [dispatch])

  const pointsEarnCalculationOptions = useMemo(
    () => [generateLoyaltyPointsCalculatorVillasOptions(offer, {
      pkg: packageView.package,
      price: packageView.hotelPrice,
      memberPrice: packageView.hotelMemberPrice,
      propertyFees: packageView.propertyFees,
      nights: packageView.package?.duration,
    })],
    [offer, packageView],
  )

  const bookNowLabel = intl.formatMessage({
    id: 'tFODLqvUp1z75npWQcMz',
    defaultMessage: 'Book now',
  })
  const checkAvailabilityLabel = intl.formatMessage({
    id: 'kR_np7lc8fnEQMUWefCY',
    defaultMessage: 'Check availability',
  })

  return <Footer>
    <Group direction="horizontal" horizontalAlign="space-between" gap={16}>
      <div>
        {showPrices && <WalledContent enforced={offer.walledGarden}>
          {!canBook && <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>}
          {canBook && <Group direction="horizontal" gap={4}>
            <FilterChip size="small" onClick={onGuestsClick}>{formatOccupantsAsGuests(...rooms)}</FilterChip>
            <FilterChip size="small" onClick={onDatesClick}>{dateLabel}</FilterChip>
          </Group>}
          <LuxPlusPriceGuard
            showMemberPrice={price.member > 0}
            fallbackPrice={<PriceRowPrice
              size="S"
              price={price.hotel}
              saleUnit="total"
            />}
          >
            <PriceRowLuxPlusPrice
              size="S"
              memberPrice={price.member}
              saleUnit="total"
              logoPlacement="leading"
            />
          </LuxPlusPriceGuard>
          {!!value && <PriceRowValueDiscountWithCaption
            data-testid="value-box"
            size="L"
            originalValue={value}
            discountPercentage={discount}
          />}
          <LoyaltyPointsEarnWithBreakdownModal
            calculationRequests={pointsEarnCalculationOptions}
            calculationType={canBook ? 'exact' : 'estimate'}
          />
        </WalledContent>}
      </div>
      <OfferBookButton
        packageView={packageView}
      >
        {canBook ? bookNowLabel : checkAvailabilityLabel}
      </OfferBookButton>
    </Group>
  </Footer>
}

export default withOfferPageAndReduxState((state, appState) => {
  return {
    offer: state.offer,
    packageView: getCheapestSelectedPackageOptionWithDefault(appState, state),
    dateLabel: getCurrentDateLabel(state),
    durationLabel: getFromDurationLabel(state, appState),
    checkInDate: state.checkInDate,
    rooms: state.rooms,
  }
}, VillasPageStickyFooter)
